import { apiDelete, apiGet, apiPost } from "../client";
import type {
  CreateForecastSnapshotRequestApi,
  DeleteForecastSnapshotResponseApi,
  ForecastSnapshotDataPointResponseApi,
  ForecastSnapshotListResponseApi,
  ForecastSnapshotResponseApi,
} from "../models/forecastSnapshot";

/**
 * Create a new point-in-time forecast snapshot.
 * POST /api/v1/forecast-snapshots
 *
 * The backend freezes the current forecast state at the moment of the call
 * and stores it against the given name/description.
 */
export async function createForecastSnapshot(
  request: CreateForecastSnapshotRequestApi,
): Promise<ForecastSnapshotResponseApi> {
  return apiPost<ForecastSnapshotResponseApi>("forecast/snapshots", request);
}

/**
 * List all available forecast snapshots for the current entity,
 * ordered most-recent first.
 * GET /api/v1/forecast-snapshots
 */
export async function getForecastSnapshots(): Promise<ForecastSnapshotListResponseApi> {
  return apiGet<ForecastSnapshotListResponseApi>("forecast/snapshots");
}

/**
 * Retrieve a single snapshot by ID (includes the full timeline data
 * needed to run a variance comparison).
 * GET /api/v1/forecast-snapshots/{id}
 */
export async function getForecastSnapshotById(
  id: number,
): Promise<ForecastSnapshotResponseApi> {
  return apiGet<ForecastSnapshotResponseApi>(`forecast/snapshots/${id}`);
}

/**
 * Retrieve the timeline data points for a specific snapshot.
 * GET /api/v1/forecast-snapshots/{id}/data-points
 */
export async function getForecastSnapshotDataPoints(
  id: number,
): Promise<ForecastSnapshotDataPointResponseApi[]> {
  return apiGet<ForecastSnapshotDataPointResponseApi[]>(
    `forecast/snapshots/${id}/data-points`,
  );
}

/**
 * Delete a snapshot by ID.
 * DELETE /api/v1/forecast-snapshots/{id}
 *
 * Only the creator or a user with manage_forecast_snapshots permission
 * should be allowed to delete — enforced server-side.
 */
export async function deleteForecastSnapshot(
  id: number,
): Promise<DeleteForecastSnapshotResponseApi> {
  return apiDelete<DeleteForecastSnapshotResponseApi>(
    `forecast/snapshots/${id}`,
  );
}
